import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import {
  FiAlertTriangle,
  FiBook,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiEdit3,
  FiExternalLink,
  FiFileText,
  FiFlag,
  FiGrid,
  FiLoader,
  FiPlus,
  FiStar,
  FiUser,
  FiX,
} from "react-icons/fi";
import { IoSparklesSharp } from "react-icons/io5";

import { auth, functions as firebaseFunctions } from "../../../firebaseConfig";
import {
  HOMEWORK_STATUS_OPTIONS,
  SESSION_TYPE_OPTIONS,
  STUDENT_FLAG_OPTIONS,
  calculateAccuracyPercent,
  fetchCurrentUserIdentity,
  loadSessionLogForm,
  toDateMaybe,
  upsertSessionLogForSession,
} from "./sessionLogApi";
import {
  ACT_TOPICS,
  SAT_PSAT_TOPICS,
  isStructuredTopicType,
  isSatPsatType,
} from "./sessionTopicData";
import {
  fetchPreviousSessionLog,
  fetchRecentStudentSessionLogs,
  fetchStudentProfile,
} from "./sessionWorkspaceData";
import { sameSubject, bareTestName, testNameIn } from "../../../utils/subjectMatch";
import CurriculumBrowserModal from "./CurriculumBrowserModal";
import SatPlanPrepCard from "./SatPlanPrepCard";
import SatPlanProgressPanel from "./SatPlanProgressPanel";
import SessionPlanMiniCard from "./SessionPlanMiniCard";
import { calcAvgRating, StarRating } from "../../session_logs/sessionRating";
import { backdropDismiss } from "../../../utils/backdropDismiss";

import "./TutorSessionLogModal.css";

// ─── Constants ────────────────────────────────────────────────────────────────

const STAR_FIELDS = [
  { key: "effort", label: "Effort" },
  { key: "motivation", label: "Motivation" },
  { key: "behavior", label: "Behavior" },
  { key: "focus", label: "Focus" },
  { key: "confidence", label: "Confidence" },
];

const STEPS = [
  { id: "prep", label: "Prepare", icon: <FiBook /> },
  { id: "session", label: "Session Info", icon: <FiGrid /> },
  { id: "materials", label: "Materials", icon: <FiFileText /> },
  { id: "notes", label: "Notes", icon: <FiEdit3 /> },
  { id: "evaluation", label: "Evaluation", icon: <FiStar /> },
  { id: "review", label: "Review & Submit", icon: <FiCheckCircle /> },
];

const LOGS_PER_PAGE = 6;

// Session types that describe the kind of session rather than a subject — they
// must not be treated as a subject when matching plans / previous sessions.
const NON_SUBJECT_SESSION_TYPES = new Set([
  "school help",
  "skill building",
  "diagnostic review",
  "homework support",
  "other",
]);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toDateMaybeLocal(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value === "object" && "seconds" in value) return new Date(value.seconds * 1000);
  const p = new Date(value);
  return Number.isNaN(p.getTime()) ? null : p;
}

function formatDate(value) {
  const d = toDateMaybeLocal(value);
  if (!d) return "—";
  return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

function formatTimeRange(start, end) {
  const fmt = (v) => {
    const d = toDateMaybeLocal(v);
    return d ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—";
  };
  return `${fmt(start)} – ${fmt(end)}`;
}

function titleCaseFirst(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
}


function localFallbackAi(mode, payload = {}) {
  const lesson = String(payload.lessonActivity || "").trim();
  const insight = String(payload.learningInsight || "").trim();
  const next = String(payload.nextFocus || "").trim();
  const hwGiven = String(payload.homeworkGiven || "").trim();

  if (mode === "rewrite_core") {
    return {
      lessonActivity: titleCaseFirst(lesson),
      learningInsight: titleCaseFirst(insight),
      nextFocus: titleCaseFirst(next),
      homeworkGiven: titleCaseFirst(hwGiven),
    };
  }

  const subject = String(payload.subject || "Subject").trim();
  const topic = String(payload.topicCovered || "current topic").trim();
  const flag = String(payload.studentFlag || "").toLowerCase();
  const ratings = [payload.effort, payload.motivation, payload.behavior, payload.focus, payload.confidence]
    .map(Number)
    .filter((v) => v > 0);
  const avg = ratings.length ? ratings.reduce((a, b) => a + b, 0) / ratings.length : 3;
  const atRisk = flag === "at risk" || avg <= 2.2;

  return {
    homeworkAssigned: titleCaseFirst(hwGiven || `Complete ${subject} practice on ${topic}.`),
    sessionSummary: titleCaseFirst(`${lesson || "Tutor reviewed key material."} ${insight || ""}`.trim()),
    nextSessionPlan: titleCaseFirst(next || `Continue ${subject} work on ${topic}.`),
    riskAlert: atRisk
      ? "Student may need immediate support. Review pacing and confidence."
      : "No urgent risk detected. Continue consistent tracking.",
  };
}

async function callSessionLogAi(mode, payload) {
  try {
    const callable = httpsCallable(firebaseFunctions, "generateSessionLogAi");
    const response = await callable({ mode, payload });
    return response.data;
  } catch {
    return localFallbackAi(mode, payload);
  }
}

function getComputedTopicCovered(form) {
  if (isStructuredTopicType(form.sessionType)) {
    return (form.selectedTopics || []).join("; ");
  }
  return form.topicCovered || "";
}

// ─── Materials helpers ──────────────────────────────────────────────────────────
// In-memory materials are objects { label, url, type, ...refs }. Older logs that
// only stored a newline string are normalized to plain-text items.
function normalizeMaterialItem(m) {
  if (typeof m === "string") {
    const label = m.trim();
    return label ? { label, url: "", type: "text" } : null;
  }
  if (!m) return null;
  const label = String(m.label || m.title || "").trim();
  if (!label) return null;
  const url = String(m.url || "").trim();
  return { ...m, label, url, type: m.type || (url ? "link" : "text") };
}

function materialLabelOf(m) {
  return typeof m === "string" ? m.trim() : String(m?.label || m?.title || "").trim();
}

function materialKey(m) {
  const item = normalizeMaterialItem(m) || {};
  if (item.type === "curriculum" && item.resourceId) return `c:${item.textbookId}:${item.resourceId}`;
  return `t:${item.label}|${item.url}`;
}

// Build the items shown in the read-only / review views from a loaded form,
// preferring the structured `materials` array, then the in-memory array, then
// the legacy newline string (older logs render as plain text).
function materialItemsFromForm(form) {
  if (Array.isArray(form?.materialUsed) && form.materialUsed.length) {
    return form.materialUsed.map(normalizeMaterialItem).filter(Boolean);
  }
  if (Array.isArray(form?.materials) && form.materials.length) {
    return form.materials.map(normalizeMaterialItem).filter(Boolean);
  }
  return String(form?.materialUsed || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((label) => ({ label, url: "", type: "text" }));
}

// Convert the in-memory materials array into the two persisted shapes:
// `materialUsed` (newline-joined labels, backward compatible) and the structured
// `materials` array used to render clickable links.
function serializeMaterials(rawMaterials) {
  const items = (Array.isArray(rawMaterials) ? rawMaterials : [])
    .map(normalizeMaterialItem)
    .filter(Boolean);
  return {
    materialUsed: items.map((m) => m.label).join("\n"),
    materials: items,
  };
}

// Validates the performance metrics. Questions Attempted must be a real count
// (>= 1) — a value of 0 (or empty) is not meaningful data. Questions Wrong must
// be entered as a valid number (0 is allowed: the student got everything right)
// and cannot exceed the number attempted. Returns the missing/invalid field
// label, or null when both are valid.
function metricsMissing(form) {
  const attempted = Number.parseInt(String(form?.questionsAttempted ?? ""), 10);
  if (!Number.isFinite(attempted) || attempted < 1) return "Questions Attempted";
  const wrong = Number.parseInt(String(form?.questionsWrong ?? ""), 10);
  if (!Number.isFinite(wrong) || wrong < 0 || wrong > attempted) return "Questions Wrong";
  return null;
}

function requiredMissing(form) {
  if (!String(form.sessionType || "").trim()) return "Session Type";

  if (isStructuredTopicType(form.sessionType)) {
    if (!(form.selectedTopics || []).length) return "Topic Covered";
  } else {
    if (!String(form.topicCovered || "").trim()) return "Topic Covered";
  }

  if (!String(form.homeworkStatus || "").trim()) return "Homework Status";

  const mats = Array.isArray(form.materialUsed)
    ? form.materialUsed.filter((m) => materialLabelOf(m))
    : String(form.materialUsed || "").trim()
    ? [form.materialUsed]
    : [];
  if (!mats.length) return "Material Used";

  const metricMiss = metricsMissing(form);
  if (metricMiss) return metricMiss;

  if (!String(form.lessonActivity || "").trim()) return "Lesson Activity";
  if (!String(form.learningInsight || "").trim()) return "Learning Insight";
  if (!String(form.nextFocus || "").trim()) return "Next Focus";
  if (!String(form.homeworkGiven || "").trim()) return "Homework Given";

  for (const { key, label } of STAR_FIELDS) {
    const val = parseInt(form[key], 10);
    if (!val || val < 1 || val > 5) return `${label} rating`;
  }

  if (!String(form.studentFlag || "").trim()) return "Student Flag";

  return null;
}

// Resolve the effective session subject: the scheduled subject is the real
// signal; the session type is only a coarse category, so generic types
// ("School Help", "Other", ...) count as "unknown subject" ("").
function resolveSessionSubject(form, session) {
  const subject = form?.auto?.subject || session?.subject || "";
  if (subject) return subject;
  const type = form?.sessionType || "";
  return NON_SUBJECT_SESSION_TYPES.has(type.trim().toLowerCase()) ? "" : type;
}

// Does a previous session log's subject match a target subject? A bare test
// name ("SAT"/"PSAT"/"ACT") matches any log that mentions that test, and a
// test named alongside a subject ("SAT Math") never matches another test's
// session ("ACT Math") even though the subject name is shared.
function logMatchesSubject(log, target) {
  const t = String(target || "").trim();
  if (!t || !log) return false;
  const hay = `${log.subject || ""} ${log.sessionType || ""}`;
  const test = bareTestName(t);
  if (test) {
    const lower = hay.toLowerCase();
    return test === "SAT" ? lower.includes("sat") : lower.includes("act");
  }
  const named = testNameIn(t);
  if (named) {
    const logTest = testNameIn(hay);
    if (logTest && logTest !== named) return false;
  }
  return sameSubject(log.subject || log.sessionType, t);
}

// Open a previous session log detail in a new tab
function openSessionDetail(log) {
  const key = `sessionLog_${log.id}`;
  const data = {
    id: log.id,
    studentId: log.studentId,
    studentName: log.studentName,
    tutorName: log.tutorName,
    subject: log.subject,
    start: log.sessionStart,
    end: log.sessionEnd,
    tutorUid: log.tutorUid,
    note: log.shortAdminNote,
  };
  try { localStorage.setItem(key, JSON.stringify(data)); } catch {}
  window.open(`/session-detail?id=${log.id}`, "_blank");
}

// ─── Draft localStorage helpers ───────────────────────────────────────────────

function getDraftKey(sessionId) {
  return `sessionDraft_${sessionId}`;
}

// Returns true when AI risk-alert text signals a genuine concern (vs "no risk" response)
function isHighRiskAlert(text) {
  if (!text?.trim()) return false;
  const t = text.trim().toLowerCase();
  return !(t.startsWith("no ") || t.startsWith("none") || t.startsWith("no urgent"));
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Req() {
  return <span className="slw-req" aria-hidden="true">*</span>;
}

function FlagBadge({ flag }) {
  if (!flag) return null;
  const cls = { "On Track": "slw-flag--good", "Needs Attention": "slw-flag--warn", "At Risk": "slw-flag--bad" }[flag] || "slw-flag--neutral";
  return <span className={`slw-flag-badge ${cls}`}>{flag}</span>;
}

function StarGroup({ fieldKey, label, value, onChange, readOnly }) {
  return (
    <div className="slw-star-group">
      <div className="slw-star-header">
        <span className="slw-star-label">{label}{!readOnly && <Req />}</span>
        <span className="slw-star-val">{value || "—"}/5</span>
      </div>
      <div className="slw-stars">
        {[1, 2, 3, 4, 5].map((score) => (
          <button
            key={score}
            type="button"
            className={`slw-star-btn${parseInt(value, 10) >= score ? " slw-star-btn--on" : ""}`}
            onClick={() => !readOnly && onChange(fieldKey, score)}
            disabled={readOnly}
            aria-label={`${label} ${score} out of 5`}
          >★</button>
        ))}
      </div>
    </div>
  );
}

// AI insight card — neutral gray, no purple
function InsightCard({ label, content, highlight }) {
  if (!content?.trim()) return null;
  return (
    <div className={`slw-insight-card${highlight ? " slw-insight-card--highlight" : ""}`}>
      <div className="slw-insight-label">{label}</div>
      <p className="slw-insight-content">{content}</p>
    </div>
  );
}

// Conference note modal
function ConferenceModal({ conference, onClose }) {
  return (
    <div className="slw-overlay-backdrop" {...backdropDismiss(onClose)}>
      <div className="slw-overlay-panel" onClick={(e) => e.stopPropagation()}>
        <div className="slw-overlay-header">
          <span className="slw-overlay-title">
            Parent Conference — {formatDate(conference?.date)}
          </span>
          <button className="slw-icon-btn" onClick={onClose} aria-label="Close"><FiX size={14} /></button>
        </div>
        <div className="slw-overlay-body slw-html-content"
          dangerouslySetInnerHTML={{ __html: conference?.note || "<p>No content.</p>" }}
        />
      </div>
    </div>
  );
}

// Multi-topic selector (SAT/PSAT/ACT) with internal pending state
function MultiTopicSelector({ sessionType, selectedTopics, onChange, readOnly }) {
  const [pendingSection, setPendingSection] = useState("");
  const [pendingDomain, setPendingDomain] = useState("");
  const [pendingSkill, setPendingSkill] = useState("");

  const isSatPsat = isSatPsatType(sessionType);
  const isAct = sessionType === "ACT";

  const sectionKeys = isSatPsat ? Object.keys(SAT_PSAT_TOPICS) : isAct ? Object.keys(ACT_TOPICS) : [];
  const domains = isSatPsat && pendingSection ? Object.keys(SAT_PSAT_TOPICS[pendingSection] || {}) : [];
  const skills = isSatPsat && pendingSection && pendingDomain
    ? SAT_PSAT_TOPICS[pendingSection]?.[pendingDomain] || []
    : isAct && pendingSection
    ? ACT_TOPICS[pendingSection] || []
    : [];

  function buildPending() {
    if (isSatPsat) {
      return [pendingSection, pendingDomain, pendingSkill].filter(Boolean).join(" > ");
    }
    if (isAct) {
      return [pendingSection, pendingDomain].filter(Boolean).join(" > ");
    }
    return "";
  }

  function handleAdd() {
    const topic = buildPending();
    if (!topic || selectedTopics.includes(topic)) return;
    onChange([...selectedTopics, topic]);
    setPendingSection("");
    setPendingDomain("");
    setPendingSkill("");
  }

  function handleRemove(topic) {
    onChange(selectedTopics.filter((t) => t !== topic));
  }

  const canAdd = isSatPsat ? !!pendingSection : isAct ? !!pendingSection : false;

  return (
    <div className="slw-topic-selector">
      {/* Selected topics chips */}
      {selectedTopics.length > 0 && (
        <div className="slw-topic-chips">
          {selectedTopics.map((topic) => (
            <span key={topic} className="slw-topic-chip">
              <span className="slw-topic-chip-text">{topic}</span>
              {!readOnly && (
                <button
                  type="button"
                  className="slw-topic-chip-remove"
                  onClick={() => handleRemove(topic)}
                  aria-label="Remove topic"
                >
                  <FiX size={10} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {!readOnly && (
        <div className="slw-topic-builder">
          {/* Section / Subject selector */}
          <select
            className="slw-select slw-select--sm"
            value={pendingSection}
            onChange={(e) => {
              setPendingSection(e.target.value);
              setPendingDomain("");
              setPendingSkill("");
            }}
          >
            <option value="">{isSatPsat ? "Section..." : "Subject..."}</option>
            {sectionKeys.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          {/* Domain selector (SAT/PSAT only) */}
          {isSatPsat && pendingSection && (
            <select
              className="slw-select slw-select--sm"
              value={pendingDomain}
              onChange={(e) => { setPendingDomain(e.target.value); setPendingSkill(""); }}
            >
              <option value="">Domain...</option>
              {domains.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          )}

          {/* Skill / Category selector */}
          {skills.length > 0 && (
            <select
              className="slw-select slw-select--sm"
              value={pendingSkill}
              onChange={(e) => setPendingSkill(e.target.value)}
            >
              <option value="">{isSatPsat ? "Skill..." : "Category..."}</option>
              {skills.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}

          <button
            type="button"
            className="slw-btn-add-topic"
            onClick={handleAdd}
            disabled={!canAdd}
          >
            <FiPlus size={13} />
            Add
          </button>
        </div>
      )}
    </div>
  );
}

function StepBar({ steps, currentStep, completedSteps, onStepClick }) {
  return (
    <div className="slw-stepbar">
      {steps.map((step, idx) => {
        const isDone = completedSteps.has(step.id);
        const isActive = step.id === currentStep;
        return (
          <React.Fragment key={step.id}>
            <button
              className={`slw-step${isActive ? " slw-step--active" : ""}${isDone ? " slw-step--done" : ""}`}
              onClick={() => onStepClick(step.id)}
              type="button"
            >
              <span className="slw-step-num">
                {isDone ? <FiCheckCircle size={12} /> : idx + 1}
              </span>
              <span className="slw-step-label">{step.label}</span>
            </button>
            {idx < steps.length - 1 && (
              <div className={`slw-step-line${isDone ? " slw-step-line--done" : ""}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function TutorSessionLogModal({ isReadOnly = false }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiBusyMode, setAiBusyMode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [form, setForm] = useState(null);
  const [currentStep, setCurrentStep] = useState("prep");
  const [autoSaveStatus, setAutoSaveStatus] = useState("");
  const [draftRestored, setDraftRestored] = useState(false);

  // Prep context
  const [prevLog, setPrevLog] = useState(null);
  const [studentProfile, setStudentProfile] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [logPage, setLogPage] = useState(0);
  // Which previous session the "From Last Session" card is previewing. Defaults
  // to the most recent session that matches the current session's subject.
  const [selectedPrevId, setSelectedPrevId] = useState(null);
  const [prepLoading, setPrepLoading] = useState(true);
  const [conferenceModal, setConferenceModal] = useState(null);

  // Curriculum picker
  const [showCurriculumPicker, setShowCurriculumPicker] = useState(false);

  // Admin entry: an admin can fill in a log on a tutor's behalf from the admin
  // portal. `adminEntry` is only true when the launcher requested it AND the
  // signed-in user really holds the admin role.
  const [adminEntry, setAdminEntry] = useState(false);
  const entryContextRef = useRef({ adminEntry: false, uid: "", name: "" });
  const entryReadyRef = useRef(null);

  const latestFormRef = useRef(null);
  const lastSavedFormRef = useRef(null);
  const draftSaveTimerRef = useRef(null);

  // ── Resolve entry context (tutor vs admin-on-behalf) ─────────────────────────
  useEffect(() => {
    let alive = true;
    const requested = new URLSearchParams(window.location.search).get("adminEntry") === "1";
    const promise = fetchCurrentUserIdentity()
      .then((identity) => {
        if (!alive) return entryContextRef.current;
        const isAdminEntry = requested && identity?.role === "admin";
        entryContextRef.current = {
          adminEntry: isAdminEntry,
          uid: identity?.uid || "",
          name: identity?.name || "",
        };
        setAdminEntry(isAdminEntry);
        return entryContextRef.current;
      })
      .catch(() => entryContextRef.current);

    // Saves await this so a very fast submit can never beat the role lookup and
    // mis-attribute an admin-written log to the tutor.
    entryReadyRef.current = promise;
    return () => { alive = false; };
  }, []);

  const resolveEntryContext = useCallback(async () => {
    if (entryReadyRef.current) {
      try { await entryReadyRef.current; } catch { /* fall through to the ref */ }
    }
    return entryContextRef.current;
  }, []);

  // ── Load session ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");
    if (!id) { setLoading(false); return; }
    const primaryKey = isReadOnly ? `sessionLog_${id}` : `tutorSession_${id}`;
    let stored = localStorage.getItem(primaryKey);
    if (!stored) stored = localStorage.getItem(isReadOnly ? `tutorSession_${id}` : `sessionLog_${id}`);
    if (stored) setSession(JSON.parse(stored));
    else setLoading(false);
  }, [isReadOnly]);

  // ── Load form ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!session?.id) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    loadSessionLogForm(session)
      .then((nextForm) => {
        if (!alive) return;
        // Hydrate materials as structured objects: prefer the saved `materials`
        // array (clickable), fall back to the legacy newline string (plain text).
        nextForm.materialUsed = materialItemsFromForm(nextForm);
        // Multi-topic: parse existing topicCovered into selectedTopics array
        if (isStructuredTopicType(nextForm.sessionType) && nextForm.topicCovered) {
          nextForm.selectedTopics = nextForm.topicCovered.split("; ").filter(Boolean);
        } else {
          nextForm.selectedTopics = [];
        }

        // Restore from localStorage draft if available
        let formToUse = nextForm;
        if (!isReadOnly) {
          try {
            const draftStr = localStorage.getItem(getDraftKey(session.id));
            if (draftStr) {
              const draft = JSON.parse(draftStr);
              if (draft?.form) {
                formToUse = draft.form;
                setDraftRestored(true);
              }
            }
          } catch {}
        }

        setForm(formToUse);
        lastSavedFormRef.current = JSON.stringify(formToUse);
        const studentName = formToUse?.auto?.studentName || session?.studentName || "";
        if (studentName) {
          document.title = `${studentName} - Session Log | True Education Cumming`;
        }
      })
      .catch(() => setErrorMessage("Could not load session log."))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [session?.id, session, isReadOnly]);

  // ── Load prep context ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!session?.studentId) { setPrepLoading(false); return; }
    let alive = true;
    setPrepLoading(true);
    Promise.all([
      fetchPreviousSessionLog(session.studentId, session.id),
      fetchStudentProfile(session.studentId),
      fetchRecentStudentSessionLogs(session.studentId, 60),
    ]).then(([prev, profile, logs]) => {
      if (!alive) return;
      setPrevLog(prev);
      setStudentProfile(profile);
      setRecentLogs(logs.filter((l) => l.id !== session.id));
    }).catch(() => {}).finally(() => { if (alive) setPrepLoading(false); });
    return () => { alive = false; };
  }, [session?.studentId, session?.id]);

  useEffect(() => { latestFormRef.current = form; }, [form]);

  // ── From Last Session: default to the previous session that matches this
  //    session's subject; fall back to the exact previous session. ─────────────
  const currentSessionSubject = resolveSessionSubject(form, session);

  useEffect(() => {
    if (!recentLogs.length) return;
    const match = currentSessionSubject
      ? recentLogs.find((l) => logMatchesSubject(l, currentSessionSubject))
      : null;
    setSelectedPrevId((match || recentLogs[0]).id);
  }, [recentLogs, currentSessionSubject]);

  // ── LocalStorage draft save (debounced 2.5s on every form change) ─────────────
  useEffect(() => {
    if (isReadOnly || !session?.id || !form) return;
    clearTimeout(draftSaveTimerRef.current);
    draftSaveTimerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(getDraftKey(session.id), JSON.stringify({ form, savedAt: Date.now() }));
        setAutoSaveStatus("Draft saved locally");
        setTimeout(() => setAutoSaveStatus((s) => (s === "Draft saved locally" ? "" : s)), 2500);
      } catch {}
    }, 2500);
    return () => clearTimeout(draftSaveTimerRef.current);
  }, [form, session?.id, isReadOnly]);

  // ── Auto-save ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isReadOnly || !session?.id) return;
    const id = setInterval(async () => {
      const f = latestFormRef.current;
      if (!f) return;
      const str = JSON.stringify(f);
      if (str === lastSavedFormRef.current) return;
      try {
        const uid = auth.currentUser?.uid || session?.tutorUid || "";
        const autoAcc = calculateAccuracyPercent(f.questionsAttempted, f.questionsWrong);
        const entry = await resolveEntryContext();
        await upsertSessionLogForSession({
          session,
          form: {
            ...f,
            topicCovered: getComputedTopicCovered(f),
            ...serializeMaterials(f.materialUsed),
            accuracyPercent: autoAcc,
          },
          currentUserUid: uid,
          source: entry.adminEntry ? "admin_form_autosave" : "tutor_form_autosave",
          enteredByAdmin: entry.adminEntry,
          enteredByAdminName: entry.name,
          enteredByAdminUid: entry.uid,
        });
        lastSavedFormRef.current = str;
      } catch {
        // Silent — localStorage draft is the user-visible backup
      }
    }, 30000);
    return () => clearInterval(id);
  }, [session, isReadOnly, resolveEntryContext]);

  // ── Computed step completion (derived from form, not navigation) ─────────────
  const completedSteps = useMemo(() => {
    if (!form) return new Set();
    const done = new Set();
    // Prepare: always complete (read-only context, no inputs)
    done.add("prep");

    const sessionDone =
      !!String(form.sessionType || "").trim() &&
      (isStructuredTopicType(form.sessionType)
        ? (form.selectedTopics || []).length > 0
        : !!String(form.topicCovered || "").trim()) &&
      !!String(form.homeworkStatus || "").trim();
    if (sessionDone) done.add("session");

    const mats = Array.isArray(form.materialUsed)
      ? form.materialUsed.filter((m) => materialLabelOf(m))
      : String(form.materialUsed || "").trim() ? [form.materialUsed] : [];
    const materialsDone = mats.length > 0 && metricsMissing(form) == null;
    if (materialsDone) done.add("materials");

    const notesDone =
      !!String(form.lessonActivity || "").trim() &&
      !!String(form.learningInsight || "").trim() &&
      !!String(form.nextFocus || "").trim() &&
      !!String(form.homeworkGiven || "").trim();
    if (notesDone) done.add("notes");

    const evalDone =
      STAR_FIELDS.every(({ key }) => {
        const v = parseInt(form[key], 10);
        return v >= 1 && v <= 5;
      }) && !!String(form.studentFlag || "").trim();
    if (evalDone) done.add("evaluation");

    if (sessionDone && materialsDone && notesDone && evalDone) done.add("review");

    return done;
  }, [form]);

  // ── Computed values ──────────────────────────────────────────────────────────
  const sessionStart = useMemo(() =>
    toDateMaybe(form?.sessionStart || form?.start || session?.sessionStart || session?.start),
    [form, session]);
  const sessionEnd = useMemo(() =>
    toDateMaybe(form?.sessionEnd || form?.end || session?.sessionEnd || session?.end),
    [form, session]);
  const accuracyPercent = useMemo(() =>
    form ? calculateAccuracyPercent(form.questionsAttempted, form.questionsWrong) : null,
    [form]);
  const roundedHours = useMemo(() => {
    const h = form?.usedHours ?? form?.auto?.usedHours ?? session?.usedHours;
    return h != null ? String(h) : "";
  }, [form, session]);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  function serializeForm(f) {
    return {
      ...f,
      topicCovered: getComputedTopicCovered(f),
      ...serializeMaterials(f.materialUsed),
      accuracyPercent,
    };
  }

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrorMessage("");
  }, []);

  const setRating = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: String(value) }));
  }, []);

  const goToStep = (stepId) => {
    setCurrentStep(stepId);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goNext = () => {
    const idx = STEPS.findIndex((s) => s.id === currentStep);
    if (idx < STEPS.length - 1) goToStep(STEPS[idx + 1].id);
  };

  const goPrev = () => {
    const idx = STEPS.findIndex((s) => s.id === currentStep);
    if (idx > 0) setCurrentStep(STEPS[idx - 1].id);
  };

  // ── AI polish ────────────────────────────────────────────────────────────────
  const handleRewriteCore = async () => {
    if (!form || aiBusyMode) return;
    setAiBusyMode("rewrite");
    try {
      const ai = await callSessionLogAi("rewrite_core", {
        lessonActivity: form.lessonActivity,
        learningInsight: form.learningInsight,
        nextFocus: form.nextFocus,
        homeworkGiven: form.homeworkGiven,
        subject: form?.auto?.subject || session?.subject,
        topicCovered: getComputedTopicCovered(form),
      });
      setForm((prev) => ({
        ...prev,
        lessonActivity: String(ai.lessonActivity || prev.lessonActivity || ""),
        learningInsight: String(ai.learningInsight || prev.learningInsight || ""),
        nextFocus: String(ai.nextFocus || prev.nextFocus || ""),
        homeworkGiven: String(ai.homeworkGiven || prev.homeworkGiven || ""),
      }));
    } catch {
      setErrorMessage("Rewrite failed. Try again.");
    } finally {
      setAiBusyMode("");
    }
  };

  // ── Submit ───────────────────────────────────────────────────────────────────
  const handleSave = async (event) => {
    event.preventDefault();
    if (!form || saving) return;
    const missing = requiredMissing(form);
    if (missing) { setErrorMessage(`"${missing}" is required before submitting.`); return; }
    setSaving(true);
    setAiBusyMode("generate");
    try {
      const uid = auth.currentUser?.uid || session?.tutorUid || "";
      const topicCovered = getComputedTopicCovered(form);
      const ai = await callSessionLogAi("generate_all", {
        subject: form?.auto?.subject || session?.subject,
        topicCovered,
        lessonActivity: form.lessonActivity,
        learningInsight: form.learningInsight,
        nextFocus: form.nextFocus,
        homeworkGiven: form.homeworkGiven,
        homeworkComments: form.homeworkComments,
        studentFlag: form.studentFlag,
        effort: form.effort,
        motivation: form.motivation,
        behavior: form.behavior,
        focus: form.focus,
        confidence: form.confidence,
        accuracyPercent,
        questionsAttempted: form.questionsAttempted,
        questionsWrong: form.questionsWrong,
        homeworkStatus: form.homeworkStatus,
        sessionType: form.sessionType,
      });
      const formWithAi = {
        ...form,
        topicCovered,
        aiHomeworkAssigned: String(ai.homeworkAssigned || ""),
        aiSessionSummary: String(ai.sessionSummary || ""),
        aiNextSessionPlan: String(ai.nextSessionPlan || ""),
        aiRiskAlert: String(ai.riskAlert || ""),
      };
      setForm(formWithAi);
      const entry = await resolveEntryContext();
      await upsertSessionLogForSession({
        session,
        form: serializeForm(formWithAi),
        currentUserUid: uid,
        source: entry.adminEntry ? "admin_form" : "tutor_form",
        enteredByAdmin: entry.adminEntry,
        enteredByAdminName: entry.name,
        enteredByAdminUid: entry.uid,
      });
      // Clear the local draft now that the log is submitted
      try { localStorage.removeItem(getDraftKey(session.id)); } catch {}
      window.close();
    } catch {
      setErrorMessage("Save failed. Please try again.");
    } finally {
      setAiBusyMode("");
      setSaving(false);
    }
  };

  // ── Curriculum ───────────────────────────────────────────────────────────────
  // The browser returns the full curriculum selection. Preserve any manually
  // typed / pasted (non-curriculum) materials and replace the curriculum set.
  const handleCurriculumConfirm = (curriculumItems) => {
    const existing = Array.isArray(form.materialUsed) ? form.materialUsed : [];
    const manual = existing.map(normalizeMaterialItem).filter((m) => m && m.type !== "curriculum");
    const seen = new Set();
    const merged = [...manual, ...(curriculumItems || []).map(normalizeMaterialItem).filter(Boolean)].filter((m) => {
      const k = materialKey(m);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    setField("materialUsed", merged);
    setShowCurriculumPicker(false);
  };

  const addManualMaterial = (rawValue) => {
    const value = String(rawValue || "").trim();
    if (!value) return;
    const isUrl = /^https?:\/\//i.test(value);
    const item = isUrl ? { label: value, url: value, type: "link" } : { label: value, url: "", type: "text" };
    const existing = Array.isArray(form.materialUsed) ? form.materialUsed : [];
    if (existing.some((m) => materialKey(m) === materialKey(item))) return;
    setField("materialUsed", [...existing, item]);
  };

  const removeMaterial = (idx) => {
    setField("materialUsed", (Array.isArray(form.materialUsed) ? form.materialUsed : []).filter((_, i) => i !== idx));
  };

  // ── READ-ONLY mode ────────────────────────────────────────────────────────────
  if (isReadOnly && form) {
    return <ReadOnlyView form={form} session={session} sessionStart={sessionStart} sessionEnd={sessionEnd} roundedHours={roundedHours} accuracyPercent={accuracyPercent} />;
  }

  if (loading || !form) {
    return (
      <div className="slw-page">
        <div className="slw-loading-state">
          <FiLoader className="slw-spin" size={22} />
          <p>Loading session workspace...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="slw-page">
        <div className="slw-loading-state"><p>Session not found.</p></div>
      </div>
    );
  }

  const pagedLogs = recentLogs.slice(logPage * LOGS_PER_PAGE, (logPage + 1) * LOGS_PER_PAGE);
  const totalLogPages = Math.ceil(recentLogs.length / LOGS_PER_PAGE);

  // ── Step renderers ────────────────────────────────────────────────────────────

  function renderPrepStep() {
    const latestConference = (() => {
      if (!Array.isArray(studentProfile?.conferences) || !studentProfile.conferences.length) return null;
      return [...studentProfile.conferences].sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0))[0] || null;
    })();

    // The card previews the most recent session that matches this session's
    // subject (falling back to the exact previous session), or whichever
    // session the tutor picked in the Recent Sessions list below.
    const defaultPrev = (() => {
      if (!recentLogs.length) return prevLog;
      const match = currentSessionSubject
        ? recentLogs.find((l) => logMatchesSubject(l, currentSessionSubject))
        : null;
      return match || recentLogs[0];
    })();
    const prevLogSelected = recentLogs.find((l) => l.id === selectedPrevId) || defaultPrev;

    return (
      <div className="slw-step-content">
        <div className="slw-step-heading">
          <h2>Session Preparation</h2>
          <p>Review what happened last session and key context before you begin.</p>
        </div>

        {prepLoading ? (
          <div className="slw-prep-loading"><FiLoader className="slw-spin" size={14} /> Loading context...</div>
        ) : (
          <div className="slw-prep-body">
            <div className="slw-prep-main">
            {/* Admin note */}
            {form?.auto?.shortAdminNote && (
              <div className="slw-banner slw-banner--warn">
                <FiFlag size={13} />
                <span><strong>Admin Note:</strong> {form.auto.shortAdminNote}</span>
              </div>
            )}

            {/* Student info row */}
            {studentProfile && (
              <div className="slw-info-row">
                <div className="slw-info-items">
                  <FiUser size={13} className="slw-info-icon" />
                  {studentProfile.schoolName && <span className="slw-info-item"><strong>School:</strong> {studentProfile.schoolName}</span>}
                  {studentProfile.schoolGrade && <span className="slw-info-item"><strong>Grade:</strong> {studentProfile.schoolGrade}</span>}
                  {studentProfile.totalSessionHours != null && <span className="slw-info-item"><strong>Total Hours:</strong> {studentProfile.totalSessionHours}</span>}
                  {studentProfile.status && <span className="slw-info-item"><strong>Status:</strong> {studentProfile.status}</span>}
                </div>
                <div className="slw-info-actions">
                  {latestConference?.note && (
                    <button
                      type="button"
                      className="slw-link-btn"
                      onClick={() => setConferenceModal(latestConference)}
                    >
                      View parent note ({formatDate(latestConference.date)})
                    </button>
                  )}
                  {session?.studentId && (
                    <a
                      href={`/students/directory/${session.studentId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="slw-link-btn"
                    >
                      <FiExternalLink size={11} /> Full profile
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Plan — inline, subject-matched, topics collapsible */}
            {session?.studentId && (
              <div className="slw-prep-plan">
                <SatPlanPrepCard
                  studentDirId={session.studentId}
                  tutorUid={auth.currentUser?.uid || session?.tutorUid || null}
                  tutorName={session?.tutorName || auth.currentUser?.displayName || null}
                  sessionLogId={session?.id || null}
                  sessionSubject={currentSessionSubject}
                />
              </div>
            )}

            {/* From last session (subject-aware; switch sessions inline below) */}
            {prevLogSelected ? (
              <div className="slw-card">
                <div className="slw-card-title-row">
                  <div className="slw-prev-meta">
                    <IoSparklesSharp size={14} />
                    <strong>From Last Session</strong>
                    <span className="slw-meta-pill">{formatDate(prevLogSelected.sessionStart)}</span>
                    {(prevLogSelected.subject || prevLogSelected.sessionType) && (
                      <span className="slw-meta-pill">
                        {prevLogSelected.subject || prevLogSelected.sessionType}
                      </span>
                    )}
                    <FlagBadge flag={prevLogSelected.studentFlag} />
                  </div>
                  <button
                    type="button"
                    className="slw-link-btn"
                    onClick={() => openSessionDetail(prevLogSelected)}
                  >
                    <FiExternalLink size={11} /> View full log
                  </button>
                </div>

                {prevLogSelected.topicCovered && (
                  <div className="slw-prev-topic">
                    <strong>Topic covered:</strong> {prevLogSelected.topicCovered}
                  </div>
                )}

                <div className="slw-insight-grid">
                  <InsightCard label="Next Session Plan" content={prevLogSelected.ai?.nextSessionPlan} highlight />
                  <InsightCard label="Session Summary" content={prevLogSelected.ai?.sessionSummary} />
                  <InsightCard label="Homework Assigned" content={prevLogSelected.ai?.homeworkAssigned} />
                  <InsightCard label="Risk Alert" content={prevLogSelected.ai?.riskAlert} />
                </div>

                {prevLogSelected.nextFocus && (
                  <div className="slw-prev-note">
                    <strong>Tutor's next focus note:</strong> {prevLogSelected.nextFocus}
                  </div>
                )}
              </div>
            ) : (
              <div className="slw-empty-card">
                <FiBook size={20} />
                <p>No previous session log found for this student.</p>
              </div>
            )}

            {/* Recent sessions — select to preview above (no popup) */}
            {recentLogs.length > 0 && (
              <div className="slw-card">
                <div className="slw-log-head-row">
                  <div className="slw-section-label">Recent Sessions</div>
                  <span className="slw-log-hint">Select a session to preview it above</span>
                </div>
                <div className="slw-log-table">
                  <div className="slw-log-header">
                    <span className="slw-log-date slw-log-col-head">Date</span>
                    <span className="slw-log-tutor slw-log-col-head">Tutor</span>
                    <span className="slw-log-subject slw-log-col-head">Subject</span>
                    <span className="slw-log-topic slw-log-col-head">Topic</span>
                    <span className="slw-log-rating slw-log-col-head">Rating</span>
                  </div>
                  {pagedLogs.map((log) => {
                    const isSelected = log.id === prevLogSelected?.id;
                    return (
                      <div
                        key={log.id}
                        className={`slw-log-row${isSelected ? " slw-log-row--selected" : ""}`}
                        onClick={() => setSelectedPrevId(log.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelectedPrevId(log.id);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        title="Click to preview this session above"
                        aria-pressed={isSelected}
                      >
                        <span className="slw-log-date">{formatDate(log.sessionStart)}</span>
                        <span className="slw-log-tutor">{log.tutorName || "—"}</span>
                        <span className="slw-log-subject">{log.subject || "—"}</span>
                        <span className="slw-log-topic">{log.topicCovered || "—"}</span>
                        <StarRating value={calcAvgRating(log)} uid={log.id} />
                        <button
                          type="button"
                          className="slw-log-link-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            openSessionDetail(log);
                          }}
                          title="Open full session log in a new tab"
                          aria-label="Open full session log"
                        >
                          <FiExternalLink size={11} />
                        </button>
                      </div>
                    );
                  })}
                </div>
                {totalLogPages > 1 && (
                  <div className="slw-pagination">
                    <button className="slw-page-btn" onClick={() => setLogPage((p) => Math.max(0, p - 1))} disabled={logPage === 0}>
                      <FiChevronLeft size={13} />
                    </button>
                    <span className="slw-page-info">{logPage + 1} / {totalLogPages}</span>
                    <button className="slw-page-btn" onClick={() => setLogPage((p) => Math.min(totalLogPages - 1, p + 1))} disabled={logPage >= totalLogPages - 1}>
                      <FiChevronRight size={13} />
                    </button>
                  </div>
                )}
              </div>
            )}

            </div>
          </div>
        )}

        {conferenceModal && (
          <ConferenceModal conference={conferenceModal} onClose={() => setConferenceModal(null)} />
        )}
      </div>
    );
  }

  function renderSessionInfoStep() {
    const useStructured = isStructuredTopicType(form.sessionType);
    return (
      <div className="slw-step-content">
        <div className="slw-step-heading">
          <h2>Session Info</h2>
          <p>Set the session type, topic, and homework review.</p>
        </div>

        <div className="slw-card">
          <h3 className="slw-card-title">Session Setup</h3>
          <div className="slw-two-col">
            <div className="slw-form-group">
              <label className="slw-label">Session Type <Req /></label>
              <select
                className="slw-select"
                value={form.sessionType}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    sessionType: e.target.value,
                    selectedTopics: [],
                    topicCovered: "",
                  }));
                  setErrorMessage("");
                }}
              >
                <option value="">Select type...</option>
                {SESSION_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            <div className="slw-form-group">
              <label className="slw-label">
                Topic Covered <Req />
                {useStructured && <span className="slw-label-hint"> — select one or more</span>}
              </label>
              {useStructured ? (
                <MultiTopicSelector
                  sessionType={form.sessionType}
                  selectedTopics={form.selectedTopics || []}
                  onChange={(topics) => setForm((prev) => ({ ...prev, selectedTopics: topics }))}
                  readOnly={false}
                />
              ) : (
                <input
                  className="slw-input"
                  type="text"
                  value={form.topicCovered}
                  onChange={(e) => setField("topicCovered", e.target.value)}
                  placeholder="e.g., Linear equations, Comma usage"
                />
              )}
            </div>
          </div>
        </div>

        <div className="slw-card">
          <h3 className="slw-card-title">Homework Review</h3>
          <div className="slw-two-col">
            <div className="slw-form-group">
              <label className="slw-label">Homework Status <Req /></label>
              <select
                className="slw-select"
                value={form.homeworkStatus}
                onChange={(e) => setField("homeworkStatus", e.target.value)}
              >
                <option value="">Select...</option>
                {HOMEWORK_STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="slw-form-group">
              <label className="slw-label">Homework Comments</label>
              <input
                className="slw-input"
                type="text"
                value={form.homeworkComments}
                onChange={(e) => setField("homeworkComments", e.target.value)}
                placeholder="Notes on completion or quality"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderMaterialsStep() {
    const materials = (Array.isArray(form.materialUsed) ? form.materialUsed : [])
      .map(normalizeMaterialItem)
      .filter(Boolean);
    return (
      <div className="slw-step-content">
        <div className="slw-step-heading">
          <h2>Materials & Metrics</h2>
          <p>Log what materials were used and how the student performed.</p>
        </div>

        <div className="slw-card">
          <h3 className="slw-card-title">Material Used <Req /></h3>

          {/* Primary action: browse the full curriculum library */}
          <button type="button" className="slw-browse-cta" onClick={() => setShowCurriculumPicker(true)}>
            <FiBook size={18} />
            <span className="slw-browse-cta-text">
              <strong>Browse Curriculum</strong>
              <span>Open any library and pick the resources used this session</span>
            </span>
            <FiChevronRight size={18} />
          </button>

          {materials.length > 0 ? (
            <div className="slw-material-list">
              {materials.map((m, i) => (
                <div key={i} className="slw-material-line">
                  {m.url ? (
                    <a className="slw-material-link" href={m.url} target="_blank" rel="noopener noreferrer" title={m.label}>
                      {m.label}
                      <FiExternalLink size={12} className="slw-material-extlink" />
                    </a>
                  ) : (
                    <span className="slw-material-text" title={m.label}>{m.label}</span>
                  )}
                  <button type="button" className="slw-material-remove" onClick={() => removeMaterial(i)} aria-label="Remove">
                    <FiX size={13} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="slw-hint">No materials added yet. Browse curriculum above, or type a resource name / paste a link below and press Enter.</p>
          )}

          <input
            className="slw-input slw-input--muted"
            type="text"
            placeholder="Type a resource name or paste a link, then press Enter…"
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target.value.trim()) {
                e.preventDefault();
                addManualMaterial(e.target.value);
                e.target.value = "";
              }
            }}
          />
        </div>

        <div className="slw-card">
          <h3 className="slw-card-title">Performance Metrics</h3>
          <div className="slw-three-col">
            <div className="slw-form-group">
              <label className="slw-label">Questions Attempted <Req /></label>
              <input className="slw-input" type="number" min="0" value={form.questionsAttempted}
                onChange={(e) => setField("questionsAttempted", e.target.value)} placeholder="0" />
            </div>
            <div className="slw-form-group">
              <label className="slw-label">Questions Wrong <Req /></label>
              <input className="slw-input" type="number" min="0" value={form.questionsWrong}
                onChange={(e) => setField("questionsWrong", e.target.value)} placeholder="0" />
            </div>
            <div className="slw-form-group">
              <label className="slw-label">Accuracy</label>
              <div className="slw-accuracy-display">
                {accuracyPercent != null ? `${accuracyPercent}%` : "—"}
              </div>
            </div>
          </div>
        </div>

        {session?.studentId && (
          <SatPlanProgressPanel
            studentDirId={session.studentId}
            tutorUid={auth.currentUser?.uid || session?.tutorUid || null}
            tutorName={session?.tutorName || auth.currentUser?.displayName || null}
            sessionLogId={session?.id || null}
            sessionSubject={currentSessionSubject}
          />
        )}

        {showCurriculumPicker && (
          <CurriculumBrowserModal
            selected={materials}
            onConfirm={handleCurriculumConfirm}
            onClose={() => setShowCurriculumPicker(false)}
          />
        )}
      </div>
    );
  }

  function renderNotesStep() {
    return (
      <div className="slw-step-content">
        <div className="slw-step-heading">
          <h2>Session Notes</h2>
          <p>Document what happened, what was learned, and what comes next.</p>
        </div>

        <div className="slw-card">
          <div className="slw-notes-grid">
            <div className="slw-form-group">
              <label className="slw-label">Lesson Activity <Req /></label>
              <textarea className="slw-textarea slw-textarea--xl" rows={10}
                value={form.lessonActivity}
                onChange={(e) => setField("lessonActivity", e.target.value)}
                placeholder="What did you cover during the session? Include specific topics, exercises, and how the student engaged. Write as much detail as you need — this becomes the core session record." />
            </div>
            <div className="slw-form-group">
              <label className="slw-label">Learning Insight <Req /></label>
              <textarea className="slw-textarea slw-textarea--xl" rows={10}
                value={form.learningInsight}
                onChange={(e) => setField("learningInsight", e.target.value)}
                placeholder="What did the student understand well? Where did they struggle? What patterns did you notice in their thinking or approach? Any breakthroughs or setbacks?" />
            </div>
            <div className="slw-form-group">
              <label className="slw-label">Next Focus <Req /></label>
              <textarea className="slw-textarea slw-textarea--lg" rows={6}
                value={form.nextFocus}
                onChange={(e) => setField("nextFocus", e.target.value)}
                placeholder="What should be prioritized in the next session? Include specific skills, topics, or strategies to revisit." />
            </div>
            <div className="slw-form-group">
              <label className="slw-label">Homework Given <Req /></label>
              <textarea className="slw-textarea slw-textarea--lg" rows={6}
                value={form.homeworkGiven}
                onChange={(e) => setField("homeworkGiven", e.target.value)}
                placeholder="What homework was assigned? Be specific about pages, problem numbers, tasks, or practice sets so the student and parent know exactly what to do." />
            </div>
          </div>

          <div className="slw-notes-footer">
            <span className="slw-notes-hint">Polish uses AI to improve grammar and clarity while preserving all your details.</span>
            <button
              type="button"
              className="slw-btn-polish"
              onClick={handleRewriteCore}
              disabled={!!aiBusyMode}
            >
              {aiBusyMode === "rewrite" ? <FiLoader className="slw-spin" size={13} /> : <IoSparklesSharp size={13} />}
              {aiBusyMode === "rewrite" ? "Polishing..." : "Polish Notes"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  function renderEvaluationStep() {
    return (
      <div className="slw-step-content">
        <div className="slw-step-heading">
          <h2>Student Evaluation</h2>
          <p>Rate the student's performance and flag their current status.</p>
        </div>

        <div className="slw-card">
          <h3 className="slw-card-title">Performance Ratings <Req /></h3>
          <div className="slw-ratings-grid">
            {STAR_FIELDS.map(({ key, label }) => (
              <StarGroup key={key} fieldKey={key} label={label} value={form[key]} onChange={setRating} readOnly={false} />
            ))}
          </div>
        </div>

        <div className="slw-card">
          <h3 className="slw-card-title">Student Flag <Req /></h3>
          <p className="slw-hint">Summarize the student's overall status this session.</p>
          <div className="slw-flag-options">
            {STUDENT_FLAG_OPTIONS.map((opt) => {
              const sel = form.studentFlag === opt;
              const mod = opt.toLowerCase().replace(/ /g, "-");
              return (
                <button
                  key={opt}
                  type="button"
                  className={`slw-flag-btn${sel ? ` slw-flag-btn--${mod}` : ""}`}
                  onClick={() => setField("studentFlag", opt)}
                >
                  {sel && <FiCheckCircle size={13} />}
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  function renderReviewStep() {
    const materials = materialItemsFromForm(form);
    // Topics: array for structured types, single string wrapped in array otherwise
    const topics = isStructuredTopicType(form.sessionType)
      ? (form.selectedTopics || [])
      : form.topicCovered?.trim()
      ? [form.topicCovered.trim()]
      : [];

    return (
      <div className="slw-step-content">
        <div className="slw-step-heading">
          <h2>Review & Submit</h2>
          <p>Confirm everything looks correct before submitting.</p>
        </div>

        {/* ── Session Info ── */}
        <div className="slw-card">
          <h3 className="slw-card-title">Session Info</h3>
          <div className="slw-review-grid">
            <ReviewBlock label="Session Type" value={form.sessionType} />
            <ReviewBlock label="Homework Status" value={form.homeworkStatus} />
            <ReviewBlock label="Questions Attempted" value={form.questionsAttempted} />
            <ReviewBlock label="Questions Wrong" value={form.questionsWrong} />
            <ReviewBlock label="Accuracy" value={accuracyPercent != null ? `${accuracyPercent}%` : null} />
            <ReviewBlock label="Student Flag" value={form.studentFlag} />
            {form.homeworkComments?.trim() && (
              <ReviewBlock label="Homework Comments" value={form.homeworkComments} wide />
            )}
          </div>
          {topics.length > 0 && (
            <div className="slw-review-section-divider">
              <ReviewTagList label={`Topic${topics.length > 1 ? "s" : ""} Covered`} items={topics} />
            </div>
          )}
        </div>

        {/* ── Materials ── */}
        {materials.length > 0 && (
          <div className="slw-card">
            <h3 className="slw-card-title">Materials Used</h3>
            <ReviewBulletList label={`${materials.length} item${materials.length !== 1 ? "s" : ""} selected`} items={materials} />
          </div>
        )}

        {/* ── Session Notes ── */}
        <div className="slw-card">
          <h3 className="slw-card-title">Session Notes</h3>
          <div className="slw-review-notes-stack">
            <ReviewNoteBlock label="Lesson Activity" value={form.lessonActivity} />
            <ReviewNoteBlock label="Learning Insight" value={form.learningInsight} />
            <ReviewNoteBlock label="Next Focus" value={form.nextFocus} />
            <ReviewNoteBlock label="Homework Given" value={form.homeworkGiven} />
          </div>
        </div>

        {/* ── Evaluation ── */}
        <div className="slw-card">
          <h3 className="slw-card-title">Student Evaluation</h3>
          <div className="slw-review-ratings-row">
            {STAR_FIELDS.map(({ key, label }) => {
              const val = parseInt(form[key], 10);
              return (
                <div key={key} className="slw-review-eval-item">
                  <span className="slw-review-rating-label">{label}</span>
                  <div className="slw-review-stars">
                    {[1,2,3,4,5].map((i) => (
                      <span key={i} className={`slw-review-star${i <= val ? " slw-review-star--on" : ""}`}>★</span>
                    ))}
                    <span className="slw-review-star-val">{val > 0 ? `${val}/5` : "—"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {errorMessage && (
          <div className="slw-alert slw-alert--error">
            <FiAlertTriangle size={14} /> {errorMessage}
          </div>
        )}
      </div>
    );
  }

  const stepContentMap = {
    prep: renderPrepStep,
    session: renderSessionInfoStep,
    materials: renderMaterialsStep,
    notes: renderNotesStep,
    evaluation: renderEvaluationStep,
    review: renderReviewStep,
  };

  const isLastStep = currentStep === "review";
  const isFirstStep = currentStep === "prep";

  return (
    <div className="slw-page">
      {/* Compact header */}
      <div className="slw-page-header">
        <div className="slw-header-brand">
          <img src="/images/logo.png" alt="True Education" className="slw-logo" />
          <div className="slw-brand-text">
            <span className="slw-brand-name">True Education</span>
            <span className="slw-brand-sub">Session Workspace</span>
          </div>
        </div>
        <div className="slw-header-center">
          <span className="slw-student-name">{form?.auto?.studentName || "—"}</span>
          <div className="slw-session-meta">
            <span>{form?.auto?.subject || "—"}</span>
            <span className="slw-dot">·</span>
            <span>{sessionStart ? formatDate(sessionStart) : "—"}</span>
            {sessionStart && sessionEnd && (
              <>
                <span className="slw-dot">·</span>
                <span>{formatTimeRange(sessionStart, sessionEnd)}{roundedHours ? ` (${roundedHours}h)` : ""}</span>
              </>
            )}
          </div>
        </div>
        <div className="slw-header-tutor">
          <span className="slw-header-label">Tutor</span>
          <span>{form?.auto?.tutorName || "—"}</span>
        </div>
      </div>

      {/* Compact stepper */}
      <div className="slw-stepper-wrapper">
        <StepBar
          steps={STEPS}
          currentStep={currentStep}
          completedSteps={completedSteps}
          onStepClick={(id) => setCurrentStep(id)}
        />
      </div>

      {/* Content */}
      <div className="slw-content">
        {adminEntry && (
          <div className="slw-banner slw-banner--admin">
            <FiUser size={13} />
            <span>
              Adding this log on behalf of <strong>{form?.auto?.tutorName || "the tutor"}</strong>. The log stays
              credited to the tutor — your name is recorded as the admin who entered it.
            </span>
          </div>
        )}
        {draftRestored && (
          <div className="slw-banner slw-banner--info">
            <FiCheckCircle size={13} />
            <span>Unsaved draft restored — your previous progress has been recovered.</span>
            <button type="button" className="slw-link-btn slw-link-btn--sm" onClick={() => setDraftRestored(false)}>Dismiss</button>
          </div>
        )}
        {errorMessage && currentStep !== "review" && (
          <div className="slw-alert slw-alert--error">
            <FiAlertTriangle size={14} /> {errorMessage}
          </div>
        )}
        {(stepContentMap[currentStep] || (() => null))()}
      </div>

      {/* Compact footer */}
      <div className="slw-footer">
        {autoSaveStatus && <span className="slw-autosave">{autoSaveStatus}</span>}
        <div className="slw-footer-actions">
          <button type="button" className="slw-btn-ghost" onClick={() => window.close()}>Cancel</button>
          {!isFirstStep && (
            <button type="button" className="slw-btn-secondary" onClick={goPrev}>
              <FiChevronLeft size={13} /> Back
            </button>
          )}
          {isLastStep ? (
            <button type="button" className="slw-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <FiLoader className="slw-spin" size={13} /> : <FiCheckCircle size={13} />}
              {saving ? "Submitting..." : "Submit Log"}
            </button>
          ) : (
            <button type="button" className="slw-btn-primary" onClick={goNext}>
              Next <FiChevronRight size={13} />
            </button>
          )}
        </div>
      </div>

      {saving && (
        <div className="slw-saving-overlay">
          <div className="slw-saving-modal">
            <FiLoader className="slw-spin" size={20} />
            <h3>Saving session log</h3>
            <p>Generating AI notes. Do not close this window.</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Review block helpers ─────────────────────────────────────────────────────

function ReviewBlock({ label, value, wide }) {
  return (
    <div className={`slw-review-block${wide ? " slw-review-block--wide" : ""}`}>
      <div className="slw-review-label">{label}</div>
      <div className="slw-review-value">{value || <span className="slw-review-empty">—</span>}</div>
    </div>
  );
}

// Renders an array of strings as pill tags (used for topics)
function ReviewTagList({ label, items }) {
  if (!items || !items.length) return null;
  return (
    <div className="slw-review-tag-block">
      <div className="slw-review-label">{label}</div>
      <div className="slw-review-tags">
        {items.map((item, i) => (
          <span key={i} className="slw-review-tag">{item}</span>
        ))}
      </div>
    </div>
  );
}

// Renders materials as a list. Each item may be a plain string (older logs) or a
// structured object { label, url } — items with a url render as a clickable link
// that opens in a new tab (Review & Submit and the admin Session Log page).
function ReviewBulletList({ label, items }) {
  if (!items || !items.length) return null;
  return (
    <div className="slw-review-bullet-block">
      <div className="slw-review-label">{label}</div>
      <ul className="slw-review-list">
        {items.map((item, i) => {
          const isObj = item && typeof item === "object";
          const text = isObj ? (item.label || item.title || "") : String(item);
          const url = isObj ? item.url : "";
          return (
            <li key={i} className="slw-review-list-item">
              {url ? (
                <a className="slw-material-link" href={url} target="_blank" rel="noopener noreferrer" title={text}>
                  {text}
                  <FiExternalLink size={12} className="slw-material-extlink" />
                </a>
              ) : (
                text
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Full-width note block with proper multiline text display
function ReviewNoteBlock({ label, value }) {
  return (
    <div className="slw-review-note-block">
      <div className="slw-review-label">{label}</div>
      <div className="slw-review-note-value">
        {value ? value : <span className="slw-review-empty">—</span>}
      </div>
    </div>
  );
}

// ─── Read-only / admin detail view ───────────────────────────────────────────
function ReadOnlyView({ form, session, sessionStart, sessionEnd, roundedHours, accuracyPercent }) {
  const materials = materialItemsFromForm(form);

  // Resolve topics array — handle both saved selectedTopics and plain topicCovered string
  const topics = (() => {
    if (Array.isArray(form.selectedTopics) && form.selectedTopics.length) {
      return form.selectedTopics;
    }
    if (form.topicCovered?.trim()) {
      if (isStructuredTopicType(form.sessionType)) {
        return form.topicCovered.split("; ").filter(Boolean);
      }
      return [form.topicCovered.trim()];
    }
    return [];
  })();

  const hasAi = form.aiSessionSummary || form.aiNextSessionPlan || form.aiHomeworkAssigned || form.aiRiskAlert;

  return (
    <div className="slw-page slw-page--readonly">
      {/* ── Header ── */}
      <div className="slw-page-header">
        <div className="slw-header-brand">
          <img src="/images/logo.png" alt="True Education" className="slw-logo" />
          <div className="slw-brand-text">
            <span className="slw-brand-name">True Education</span>
            <span className="slw-brand-sub">Session Log</span>
          </div>
        </div>
        <div className="slw-header-center">
          <span className="slw-student-name">{form?.auto?.studentName || "—"}</span>
          <div className="slw-session-meta">
            <span>{form?.auto?.subject || "—"}</span>
            <span className="slw-dot">·</span>
            <span>{sessionStart ? formatDate(sessionStart) : "—"}</span>
            {sessionStart && sessionEnd && (
              <>
                <span className="slw-dot">·</span>
                <span>
                  {sessionStart.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} –{" "}
                  {sessionEnd.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                  {roundedHours ? ` (${roundedHours}h)` : ""}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="slw-header-tutor">
          <span className="slw-header-label">Tutor</span>
          <span>{form?.auto?.tutorName || "—"}</span>
        </div>
      </div>

      <div className="slw-content slw-content--readonly">

        {/* ── Entry attribution — shown when an admin authored the log ── */}
        {form.entryMeta?.enteredByAdmin && (
          <div className="slw-banner slw-banner--admin">
            <FiUser size={13} />
            <span>
              Entered by admin
              {form.entryMeta.enteredByAdminName ? ` ${form.entryMeta.enteredByAdminName}` : ""} on behalf of{" "}
              <strong>{form?.auto?.tutorName || "the tutor"}</strong>.
            </span>
          </div>
        )}

        {/* ── AI Overview — topmost, most prominent ── */}
        {hasAi && (
          <div className="slw-ro-ai-section">
            <div className="slw-ro-section-head">
              <IoSparklesSharp size={13} />
              <span>AI Overview</span>
            </div>

            {/* Session Summary — hero card */}
            {form.aiSessionSummary && (
              <div className="slw-ro-hero">
                <div className="slw-ro-ai-label">Session Summary</div>
                <p className="slw-ro-hero-text">{form.aiSessionSummary}</p>
              </div>
            )}

            {/* Secondary AI insights — stacked vertically */}
            <div className="slw-ro-ai-stack">
              {form.aiNextSessionPlan && (
                <div className="slw-ro-ai-card slw-ro-ai-card--accent">
                  <div className="slw-ro-ai-label">Next Session Plan</div>
                  <p className="slw-ro-ai-text">{form.aiNextSessionPlan}</p>
                </div>
              )}
              {form.aiHomeworkAssigned && (
                <div className="slw-ro-ai-card">
                  <div className="slw-ro-ai-label">Homework Assigned</div>
                  <p className="slw-ro-ai-text">{form.aiHomeworkAssigned}</p>
                </div>
              )}
              {form.aiRiskAlert && (
                <div className={`slw-ro-ai-card ${isHighRiskAlert(form.aiRiskAlert) ? "slw-ro-ai-card--warn" : "slw-ro-ai-card--ok"}`}>
                  <div className="slw-ro-ai-label">Risk Alert</div>
                  <p className="slw-ro-ai-text">{form.aiRiskAlert}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Plan progress — compact, subject-matched, read-only ── */}
        {session?.studentId && (
          <SessionPlanMiniCard
            studentDirId={session.studentId}
            sessionSubject={resolveSessionSubject(form, session)}
          />
        )}

        {/* ── Session Info ── */}
        <div className="slw-card">
          <h3 className="slw-card-title">Session Info</h3>
          <div className="slw-review-grid">
            <ReviewBlock label="Session Type" value={form.sessionType} />
            <ReviewBlock label="Homework Status" value={form.homeworkStatus} />
            <ReviewBlock label="Questions Attempted" value={form.questionsAttempted} />
            <ReviewBlock label="Questions Wrong" value={form.questionsWrong} />
            <ReviewBlock label="Accuracy" value={accuracyPercent != null ? `${accuracyPercent}%` : null} />
            <div className="slw-review-block">
              <div className="slw-review-label">Student Flag</div>
              <div style={{ marginTop: 2 }}>
                {form.studentFlag
                  ? <FlagBadge flag={form.studentFlag} />
                  : <span className="slw-review-empty">—</span>}
              </div>
            </div>
            {form.homeworkComments?.trim() && (
              <ReviewBlock label="Homework Comments" value={form.homeworkComments} wide />
            )}
          </div>

          {topics.length > 0 && (
            <div className="slw-review-section-divider">
              <ReviewTagList
                label={`Topic${topics.length > 1 ? "s" : ""} Covered`}
                items={topics}
              />
            </div>
          )}

          {materials.length > 0 && (
            <div className="slw-review-section-divider">
              <ReviewBulletList
                label={`${materials.length} material${materials.length !== 1 ? "s" : ""} used`}
                items={materials}
              />
            </div>
          )}
        </div>

        {/* ── Session Notes ── */}
        <div className="slw-card">
          <h3 className="slw-card-title">Session Notes</h3>
          <div className="slw-review-notes-stack">
            <ReviewNoteBlock label="Lesson Activity" value={form.lessonActivity} />
            <ReviewNoteBlock label="Learning Insight" value={form.learningInsight} />
            <ReviewNoteBlock label="Next Focus" value={form.nextFocus} />
            <ReviewNoteBlock label="Homework Given" value={form.homeworkGiven} />
          </div>
        </div>

        {/* ── Student Evaluation ── */}
        <div className="slw-card">
          <h3 className="slw-card-title">Student Evaluation</h3>
          <div className="slw-review-ratings-row">
            {STAR_FIELDS.map(({ key, label }) => {
              const val = parseInt(form[key], 10);
              return (
                <div key={key} className="slw-review-eval-item">
                  <span className="slw-review-rating-label">{label}</span>
                  <div className="slw-review-stars">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <span key={i} className={`slw-review-star${i <= val ? " slw-review-star--on" : ""}`}>★</span>
                    ))}
                    <span className="slw-review-star-val">{val > 0 ? `${val}/5` : "—"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>

      <div className="slw-footer">
        <div className="slw-footer-actions">
          <button type="button" className="slw-btn-ghost" onClick={() => window.close()}>Close</button>
        </div>
      </div>
    </div>
  );
}

